/**
 * `/api/libraries`, `/api/resources`, `/api/trash` and `/api/search`.
 *
 * Every response carries `capabilities` — what *this caller* may do with *this
 * resource*. Screens render their buttons from that and never work access out
 * for themselves, so the interface can never disagree with the server.
 *
 * This is also the one module that reaches past `apiFetch`, for the upload. The
 * reason is in `uploadFile` below.
 */
import type {
  CreateFolderRequest,
  GrantDto,
  GrantListResponse,
  LibraryListResponse,
  ListQueryParams,
  ResourceKind,
  ResourceListResponse,
  ResourceResponse,
  ResourceSearchResponse,
  RestoreResponse,
  ShareRequest,
  SubjectType,
  TransferOwnershipRequest,
  TrashListResponse,
  UpdateResourceRequest,
  VersionDto,
  VersionListResponse,
} from '@forsys/shared';
import { API_BASE_PATH } from '@forsys/shared';
import { ApiError, apiFetch, apiUrl, query } from './client';

// --- libraries --------------------------------------------------------------

export function listLibraries(): Promise<LibraryListResponse> {
  return apiFetch<LibraryListResponse>('/libraries');
}

/** The library's root folder, created on first access. The entry point to a tree. */
export function getLibraryRoot(key: string): Promise<ResourceResponse> {
  return apiFetch<ResourceResponse>(`/libraries/${encodeURIComponent(key)}/root`);
}

// --- the tree ---------------------------------------------------------------

export function getResource(id: string): Promise<ResourceResponse> {
  return apiFetch<ResourceResponse>(`/resources/${id}`);
}

export function listChildren(
  id: string,
  options: ListQueryParams = {},
): Promise<ResourceListResponse> {
  return apiFetch<ResourceListResponse>(`/resources/${id}/children${query({ ...options })}`);
}

export function createFolder(
  parentId: string,
  body: CreateFolderRequest,
): Promise<ResourceResponse> {
  return apiFetch<ResourceResponse>(`/resources/${parentId}/folders`, {
    method: 'POST',
    json: body,
  });
}

/** Rename, move, or both. The backend rejects a body with neither. */
export function updateResource(id: string, body: UpdateResourceRequest): Promise<ResourceResponse> {
  return apiFetch<ResourceResponse>(`/resources/${id}`, { method: 'PATCH', json: body });
}

/** Moves to the trash. Recoverable — this is not a purge. */
export function trashResource(id: string): Promise<{ trashed: number }> {
  return apiFetch<{ trashed: number }>(`/resources/${id}`, { method: 'DELETE' });
}

export function transferOwnership(
  id: string,
  body: TransferOwnershipRequest,
): Promise<ResourceResponse> {
  return apiFetch<ResourceResponse>(`/resources/${id}/transfer`, { method: 'POST', json: body });
}

// --- content ----------------------------------------------------------------

export interface UploadOptions {
  /** How much of the body has been sent, 0–1. Only fires when the length is known. */
  onProgress?: (fraction: number) => void;
  /** Handed the abort function before the first byte leaves, so a caller can cancel. */
  onStart?: (abort: () => void) => void;
}

/**
 * Uploads a file into a folder. The raw body **is** the content.
 *
 * This is the one call in the app that does not go through `apiFetch`, and the
 * reason is progress: `fetch` cannot report how much of a request body has been
 * sent, so a 500 MB upload would be a spinner with no end in sight.
 * `XMLHttpRequest` still can, so it is the right tool here and only here.
 *
 * The name travels in a header rather than a multipart part, because the server
 * streams the body straight to disk — see the routes README. It is
 * percent-encoded on the way out: a header may only carry ASCII, and file names
 * are full of everything else.
 */
export function uploadFile(
  parentId: string,
  file: File,
  options: UploadOptions = {},
): Promise<ResourceResponse> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', apiUrl(`/resources/${parentId}/files`));
    request.withCredentials = true;
    request.setRequestHeader('Accept', 'application/json');
    request.setRequestHeader('X-File-Name', encodeURIComponent(file.name));
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) options.onProgress?.(event.loaded / event.total);
    });

    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) {
        resolve(JSON.parse(request.responseText) as ResourceResponse);
        return;
      }
      reject(errorFromXhr(request));
    });

    request.addEventListener('error', () => {
      reject(new ApiError(0, 'network_error', 'The upload could not reach the server.'));
    });
    request.addEventListener('abort', () => {
      reject(new ApiError(0, 'aborted', 'The upload was cancelled.'));
    });

    options.onStart?.(() => request.abort());
    request.send(file);
  });
}

/** Mirrors `readError` in the client: the body may not be our error shape. */
function errorFromXhr(request: XMLHttpRequest): ApiError {
  try {
    const body = JSON.parse(request.responseText) as { error?: { code: string; message: string } };
    if (body.error?.code && body.error.message) {
      return new ApiError(request.status, body.error.code, body.error.message);
    }
  } catch {
    // Not JSON. Fall through.
  }
  return new ApiError(request.status, 'unknown_error', `The upload failed (${request.status}).`);
}

/**
 * Where the browser should go to download a file.
 *
 * A URL rather than a request: the response carries `Content-Disposition`, so
 * letting the browser fetch it directly gives a real download with a progress
 * indicator and a file on disk. Reading it into memory to re-offer it as a blob
 * would be strictly worse, and would break for anything large.
 */
export function contentUrl(id: string, versionId?: string): string {
  return `${API_BASE_PATH}/resources/${id}/content${query({ version: versionId })}`;
}

export function listVersions(id: string): Promise<VersionListResponse> {
  return apiFetch<VersionListResponse>(`/resources/${id}/versions`);
}

/** Restoring an old version writes a **new** one on top. Nothing is rewritten. */
export function restoreVersion(id: string, versionId: string): Promise<{ version: VersionDto }> {
  return apiFetch<{ version: VersionDto }>(`/resources/${id}/versions/${versionId}/restore`, {
    method: 'POST',
  });
}

// --- sharing ----------------------------------------------------------------

export function listGrants(id: string): Promise<GrantListResponse> {
  return apiFetch<GrantListResponse>(`/resources/${id}/grants`);
}

/** Adds or replaces one subject's grant. Grants only ever **add** access. */
export function share(id: string, body: ShareRequest): Promise<{ grant: GrantDto }> {
  return apiFetch<{ grant: GrantDto }>(`/resources/${id}/grants`, { method: 'PUT', json: body });
}

export function unshare(
  id: string,
  subjectType: SubjectType,
  subjectId: number,
): Promise<{ removed: boolean }> {
  return apiFetch<{ removed: boolean }>(`/resources/${id}/grants/${subjectType}/${subjectId}`, {
    method: 'DELETE',
  });
}

/**
 * Stops a folder inheriting from above.
 *
 * Its own call and its own permission, deliberately apart from sharing: the
 * current grants are copied down first, so **nobody loses access at this
 * moment**. `copied` says how many came with it.
 */
export function breakInheritance(id: string): Promise<{ copied: number }> {
  return apiFetch<{ copied: number }>(`/resources/${id}/inheritance/break`, { method: 'POST' });
}

export function restoreInheritance(id: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/resources/${id}/inheritance/restore`, { method: 'POST' });
}

// --- trash ------------------------------------------------------------------

/** Owner-scoped: what you own, or what you deleted. Not everyone's trash. */
export function listTrash(
  library?: string,
  params: ListQueryParams = {},
): Promise<TrashListResponse> {
  return apiFetch<TrashListResponse>(`/trash${query({ library, ...params })}`);
}

export function restoreFromTrash(id: string): Promise<RestoreResponse> {
  return apiFetch<RestoreResponse>(`/trash/${id}/restore`, { method: 'POST' });
}

// --- search -----------------------------------------------------------------

/**
 * The shortest query the endpoint will answer.
 *
 * `searchResources` in the backend rejects anything shorter with
 * `query_too_short`, because one character matches most of a library. It lives
 * here rather than in a screen because both the header box and the search page
 * gate on it — and it is duplicated across the wire on purpose: a public
 * endpoint cannot trust a client to clamp. If one moves, move the other.
 */
export const MIN_SEARCH_LENGTH = 2;

export interface SearchParams {
  q: string;
  library?: string;
  kind?: ResourceKind;
  /** A folder id to search beneath. */
  under?: string;
  limit?: number;
}

/**
 * Finds resources by **name** — not by content.
 *
 * There is no paging: results are filtered by what the caller may read *after*
 * the database match, so an offset would not mean the same thing twice.
 * `hasMore` true means the query was too broad to answer completely.
 */
export function searchResources(params: SearchParams): Promise<ResourceSearchResponse> {
  return apiFetch<ResourceSearchResponse>(`/search${query({ ...params })}`);
}
